<template>
  <form @submit.prevent="submit" class="uu-form">
    <div class="uu-warn">
      <div class="warn-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 9v4M12 17h.01" stroke-linecap="round" />
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="warn-copy">
        <div class="warn-title">Correct the UTR on this approved order</div>
        <div class="warn-body">
          The merchant has already received a callback with the old UTR. Saving a new value here fires a fresh callback with the corrected UTR so the merchant's record matches reality.
        </div>
      </div>
    </div>

    <div class="uu-summary">
      <div class="sum-row">
        <span class="sum-label">Order</span>
        <span class="sum-value">{{ orderRow.merchantOrderId || orderRow.refID }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Current UTR</span>
        <span class="sum-value mono break">{{ orderRow.transactionID || '—' }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Bank</span>
        <span class="sum-value">{{ orderRow.validatorUsername || '—' }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Amount</span>
        <span class="sum-value">₹{{ formatAmount(orderRow.amount) }}</span>
      </div>
    </div>

    <div class="field">
      <label>New UTR <span class="req">*</span></label>
      <div class="input-wrap">
        <input
          v-model.trim="newUtr"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="22"
          class="input mono"
          placeholder="Enter the corrected UTR (digits only)"
          autofocus
          @input="filterUtrInput"
        />
      </div>
    </div>

    <div class="field">
      <label>Reason <span class="req">*</span></label>
      <textarea
        v-model.trim="reason"
        rows="2"
        maxlength="500"
        class="textarea"
        placeholder="Why is the UTR being corrected? (e.g. typo at approval, wrong UTR captured)"
      ></textarea>
      <div class="field-foot">
        <span class="field-hint">Stored in the audit log.</span>
        <span class="char-count">{{ reason.length }}/500</span>
      </div>
    </div>

    <p v-if="errorMsg" class="form-alert">{{ errorMsg }}</p>

    <div class="form-actions">
      <button
        type="button"
        class="btn btn-secondary"
        @click="$emit('close')"
        :disabled="isSubmitting"
      >Cancel</button>
      <button
        type="submit"
        class="btn btn-warn"
        :disabled="isSubmitting || !canSubmit"
      >
        <span v-if="!isSubmitting">Update UTR</span>
        <span v-else class="btn-loading">
          <span class="spinner-mini"></span>
          Saving…
        </span>
      </button>
    </div>
  </form>
</template>

<script>
export default {
  name: "update-utr-form",
  props: {
    orderRow: { type: Object, required: true },
  },
  emits: ["close", "updated"],
  data() {
    return {
      newUtr: "",
      reason: "",
      errorMsg: null,
      isSubmitting: false,
    };
  },
  computed: {
    canSubmit() {
      const trimmed = (this.newUtr || '').trim();
      const reasonTrimmed = (this.reason || '').trim();
      return !!trimmed
        && !!reasonTrimmed
        && trimmed !== (this.orderRow.transactionID || '');
    },
  },
  methods: {
    formatAmount(n) {
      const v = parseFloat(n || 0);
      return Number.isFinite(v) ? v.toFixed(2) : '0.00';
    },
    // Strip non-digit characters as the vendor types / pastes. Same
    // filter as the customer payment page and the operator approval /
    // add-UTR forms.
    filterUtrInput(event) {
      const raw = event.target.value;
      const cleaned = raw.replace(/[^0-9]/g, '');
      if (cleaned !== raw) {
        event.target.value = cleaned;
        this.newUtr = cleaned;
      }
    },
    async submit() {
      this.errorMsg = null;
      // Belt-and-suspenders re-strip before submit catches anything that
      // bypassed @input (autofill, programmatic write).
      this.newUtr = String(this.newUtr || '').replace(/[^0-9]/g, '');
      if (!this.canSubmit) {
        this.errorMsg = "Enter a new UTR (digits only, different from the current one) and a reason.";
        return;
      }
      this.isSubmitting = true;
      try {
        await this.$store.dispatch("payin/updatePayInUtr", {
          refID: this.orderRow.refID,
          transactionID: this.newUtr.trim(),
          reason: this.reason.trim(),
        });
        this.$emit("updated", "UTR updated. Merchant has been notified.");
      } catch (e) {
        this.errorMsg = e.message || "Failed to update UTR.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.uu-form { display: flex; flex-direction: column; gap: 14px; }

.uu-warn {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  color: #78350f;
}
.warn-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: #fde68a;
  color: #b45309;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.warn-icon svg { width: 18px; height: 18px; }
.warn-copy { flex: 1; min-width: 0; font-size: 12.5px; line-height: 1.55; }
.warn-title { font-weight: 700; font-size: 13px; margin-bottom: 4px; color: #92400e; }

.uu-summary {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sum-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; }
.sum-value.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; color: #334155; }
.sum-value.break { word-break: break-all; overflow-wrap: anywhere; }

.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.req { color: #dc2626; }
.input-wrap { position: relative; display: flex; align-items: center; }
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  letter-spacing: 0.05em;
}
.input.mono { font-family: "SFMono-Regular", Menlo, monospace; }
.input:focus {
  border-color: #b45309;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(180, 83, 9, 0.10);
}
.textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.textarea:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.field-foot {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 6px;
  font-size: 11px;
}
.field-hint { color: #94a3b8; }
.char-count { color: #94a3b8; font-variant-numeric: tabular-nums; }

.form-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  margin: 0;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-warn {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(180, 83, 9, 0.45);
}
.btn-warn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(180, 83, 9, 0.55);
}
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
